/**
 * Planner reconciliation after Flux Agent moves something on the user's schedule. Re-reads
 * activities and the schedule/ghost-block projection and writes them into the shared stores, so
 * calendar views reflect background reschedules without a page reload. Activity writes are
 * snapshot-guarded so a newer local edit or an account switch during the read is not overwritten.
 *
 * @module apps/web/src/components/flux-agent/plannerRefresh
 */

import { notifyKanbanChanged } from '@/lib/kanbanApi';
import { api } from '@/lib/api';
import { taskToUI, type ScheduleBlock } from '@fluxability/types';
import type { GhostBlocksData } from '@fluxability/types';
import { captureTaskSnapshot, isTaskSnapshotCurrent, useTasksStore } from '@/stores/tasksStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { captureCalendarReadFence } from '@/lib/browserGhostBlocks';
import type { FluxAgentAction } from '@/lib/fluxAgentApi';

/**
 * Map the `getGhostBlocks` payload into calendar blocks: ghost suggestions first, then work
 * segments, with `pause` segments shown as low-priority "Free time" needing no energy.
 */
function scheduleBlocks(
  data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>
): ScheduleBlock[] {
  const ghostBlocks = (data.ghost_blocks || []).map(block => ({
    id: block.id,
    taskId: block.task_id,
    title: block.title || 'Suggested activity',
    startTime: block.proposed_start,
    endTime: block.proposed_end,
    isGhost: true,
  }));
  const workSegments = (data.work_segments || []).filter(segment => segment.segment_type !== 'pause').map(segment => ({
    id: segment.id,
    sessionId: segment.session_id,
    workSegmentIndex: segment.segment_index,
    workSegmentCount: segment.segment_count,
    taskId: segment.task_id ?? '',
    title: segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: 3,
  }));
  return [...ghostBlocks, ...workSegments];
}

/** Map the payload's `ghost_blocks` into the schedule store's camelCase ghost-block records. */
function ghostBlocks(data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>) {
  return (data.ghost_blocks || []).map(block => ({
    id: block.id,
    taskId: block.task_id,
    proposedStart: block.proposed_start,
    proposedEnd: block.proposed_end,
    cost: block.cost,
    confidence: block.confidence,
    reason: block.reason,
  }));
}

/**
 * Refresh activities and schedule blocks from the API. Failures are logged and leave the current
 * store contents in place; the calendar views also reload on their next mount.
 */
export async function refreshPlannerAfterAgent(): Promise<void> {
  const snapshot = captureTaskSnapshot();
  const currentCalendar = captureCalendarReadFence();
  try {
    const [taskResponse, scheduleResponse] = await Promise.all([
      api.tasks.list(),
      api.schedule.getGhostBlocks(),
    ]);
    if (!isTaskSnapshotCurrent(snapshot) || !currentCalendar()) return;
    if (taskResponse.success && taskResponse.data) {
      const list = Array.isArray(taskResponse.data) ? taskResponse.data : taskResponse.data?.tasks;
      if (Array.isArray(list)) useTasksStore.getState().setTasks(list.map(taskToUI), snapshot);
    }
    if (scheduleResponse.success && scheduleResponse.data) {
      const schedule = useScheduleStore.getState();
      schedule.setBlocks(scheduleBlocks(scheduleResponse.data));
      schedule.setGhostBlocks(ghostBlocks(scheduleResponse.data));
    }
  } catch (error) {
    console.error('Failed to refresh the planner after Flux Agent changes:', error);
  }
}

/**
 * Bring the app up to date after Flux Agent acted, by what it touched: activities and schedule
 * (`schedule`, `session`, `workspace`, and `energy`, because a recorded P/RJ reading moves the
 * near-term energy the ghost suggestions are placed on), the project and bundle workspace (`workspace`), and the
 * open immersion panels (`session`: started sessions open, finished or cancelled ones close).
 * Realtime events cover only activities, so the workspace and sessions are re-read here. Failed,
 * pending and cancelled actions trigger nothing. Never throws.
 */
export async function refreshAfterAgentActions(
  actions: Pick<FluxAgentAction, 'kind' | 'status'>[]
): Promise<void> {
  const touched = new Set(
    actions.filter(action => action.status === 'completed').map(action => action.kind)
  );
  const work: Promise<unknown>[] = [];
  if (touched.has('workspace')) notifyKanbanChanged();
  if (
    touched.has('schedule') ||
    touched.has('session') ||
    touched.has('workspace') ||
    touched.has('energy')
  )
    work.push(refreshPlannerAfterAgent());
  if (touched.has('workspace'))
    work.push(
      useWorkspaceStore
        .getState()
        .loadWorkspace()
        .catch(() => undefined)
    );
  if (touched.has('session')) work.push(syncImmersionPanels());
  await Promise.all(work);
}

/** Open an immersion panel for every active server session and close panels whose session ended. */
async function syncImmersionPanels(): Promise<void> {
  try {
    const response = await api.training.getActiveImmersions();
    if (!response.success || !Array.isArray(response.data)) return;
    const active = new Set(
      response.data.flatMap(session => (session.task_id ? [session.task_id] : []))
    );
    const schedule = useScheduleStore.getState();
    for (const taskId of active) {
      if (!schedule.activeImmersionTaskIds.includes(taskId)) schedule.startImmersionMode(taskId);
    }
    for (const taskId of schedule.activeImmersionTaskIds) {
      if (!active.has(taskId)) schedule.endImmersionMode(taskId);
    }
  } catch (error) {
    console.error('Failed to refresh activity sessions after Flux Agent changes:', error);
  }
}
